<template>
  <div class="customer-Edit-dialog modal-footer">
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        data-cy="customer-edit-dialog"
        class="customer-Edit-dialog !w-[80%] !max-w-[80%]"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle>
            <h2>{{ $t("customers.edit") }}</h2>
          </SDialogTitle>
          <SSeparator class="mb-0" />
        </SDialogHeader>
        <el-scrollbar :max-height="maxHeight">
          <SField
            v-if="!isAssignedCustomer"
            orientation="horizontal"
            class="mx-1.5 mb-4 w-fit rounded-md border px-3 py-2"
          >
            <SCheckbox
              id="save-and-assign"
              data-cy="customer-edit-save-and-assign"
              v-model="saveAndAssign"
            />
            <SFieldLabel for="save-and-assign" class="font-normal">
              {{ $t("customers.assign-to-order") }}
            </SFieldLabel>
          </SField>
          <CustomerEditForm ref="customerEditForm"></CustomerEditForm>
        </el-scrollbar>

        <SDialogFooter class="gap-3">
          <!-- APPS-689: from the order's customer, the list is where it is switched -->
          <SButton
            v-if="isAssignedCustomer"
            type="button"
            variant="outline"
            class="min-w-[120px]"
            data-cy="customer-edit-change-customer-button"
            @click="toggleModalVisible(customerModalStatus)"
          >
            {{ $t("customers.change-customer") }}
          </SButton>
          <SButton
            v-else
            type="button"
            variant="outline"
            class="min-w-[120px]"
            data-cy="customer-edit-go-back-button"
            @click="toggleModalVisible(customerModalStatus)"
          >
            {{ $t("button.go-back") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            data-cy="customer-edit-save-button"
            @click="onSave()"
          >
            {{ $t("button.save") }}
          </SButton>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import UserModule from "@/store/modules/user";
import CustomersModule from "@/store/modules/customers";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { convertIdToCodeLang } from "@/utils/helpers/lang";
import NotificationService from "@/services/notification.service";
import { toggleModalVisible } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import { LogService } from "@/services/logging";
import { useOrderStore } from "@smeetz/order-store";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SField,
  SFieldLabel,
  SSeparator,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "CustomerEdit",
  components: {
    SButton,
    SCheckbox,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SField,
    SFieldLabel,
    SSeparator,
  },
  setup() {
    const modalVisibility = dialogVisibleForStatus(ModalStatus.EditCustomer);
    const customerEditForm = ref();
    let isLoading = ref<boolean>(false);
    const maxHeight = "50vh";
    // The edit dialog is mounted fresh each time it opens (dynamic modal
    // component), so whether this is the order's customer is read once here.
    // APPS-689: the order's customer cannot be unassigned from this dialog,
    // so the "Assign to order" checkbox is only offered for another customer,
    // where checking it switches the order to them.
    const assignedCustomerId = CustomersModule.getSelectedCustomerId;
    const isAssignedCustomer =
      assignedCustomerId !== null &&
      String(assignedCustomerId) ===
        String(CustomersModule.editedCustomer[0]?.id);
    const saveAndAssign = ref(false);

    const onSave = async () => {
      if (!customerEditForm.value) return;
      isLoading.value = true;

      const { valid, values } = await customerEditForm.value.validate();

      if (!valid) {
        isLoading.value = false;
        NotificationService.showCommonWarningValidation();
        return;
      }

      const { language, ...data } = values;

      const params = {
        groupId: UserModule.getUserGroupId,
        customerId: CustomersModule.editedCustomer[0].id,
        ...data,
        language: convertIdToCodeLang(language),
      };

      LogService.info(`Patching the customer: `, {
        params,
      });
      let customer = await CustomersModule.patchCustomer(params);

      const {
        country,
        email,
        firstName,
        lastName,
        phone,
        id,
        company,
        address,
        allergies,
        title,
        zipCode,
        emailMarketingOrganiser,
        birthDate,
        city,
      } = customer;
      await CustomersModule.setEditedCustomers([
        {
          country,
          email,
          fullName: `${firstName} ${lastName}`,
          phone,
          id: String(id),
          checkOrder: true,
          company,
          address,
          allergies,
          title,
          zipCode,
          emailMarketingOrganiser,
          birthDate,
          language,
          city,
        },
      ]);
      // The order's customer is always re-assigned, so the edited values reach
      // the order. Another customer is assigned only when the "Assign to order"
      // checkbox is checked, which switches the order to them.
      const orderStore = useOrderStore();
      const orderId = orderStore.selectedOrder?.id;
      const assignedId = CustomersModule.getSelectedCustomerId;
      const isAssigned =
        assignedId !== null && String(assignedId) === String(values.id);

      if (isAssigned || saveAndAssign.value) {
        await CustomersModule.setSelectedCustomers([
          {
            country,
            email,
            fullName: `${firstName} ${lastName}`,
            phone,
            id: String(id),
            checkOrder: true,
            orderId: orderId,
            company,
            address,
            allergies,
            language,
            title,
            zipCode,
            emailMarketingOrganiser,
            birthDate,
            city,
          },
        ]);
      }

      isLoading.value = false;
      hideDialog();
    };
    const customerModalStatus = ModalStatus.Customers;

    return {
      modalVisibility,
      customerEditForm,
      toggleModalVisible,
      isLoading,
      onSave,
      maxHeight,
      customerModalStatus,
      saveAndAssign,
      isAssignedCustomer,
    };
  },
});
</script>
